import { Box, Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ROLES } from 'src/constants'

export default function OrderViewDetails({ tempOrder, order, isSatchelShipment }) {
    const { t } = useTranslation()
    const { profile } = useSelector((state) => state.entities.profile)

    const shipment = order?.otoIntegration?.returnShipment ?? order?.otoIntegration?.shipment ?? null

    return (
        <Box>
            <Grid container spacing={2}>
                {/* Order Header Information */}
                <Grid item xs={12}>
                    <Typography variant="h5" gutterBottom>
                        {t("Order Details")}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {t("Order No.")}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {order?.orderNumber || "-"}
                        </Typography>
                    </Box>
                </Grid>

                {profile?.role === ROLES.admin && (
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                {"ID"}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {order?.id || "-"}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                {shipment?.trackingNumber && (
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                {t("Shipment Number")}
                            </Typography>
                            {shipment?.trackingUrl ? (
                                <a
                                    href={shipment.trackingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                >
                                    <Typography variant="body1" fontWeight="bold" color="primary">
                                        {shipment.trackingNumber}
                                    </Typography>
                                </a>
                            ) : (
                                <Typography variant="body1" fontWeight="bold">
                                    {shipment.trackingNumber}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                )}

                {order?.merchantOrderNumber && (
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                {t("Order reference number")}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {order.merchantOrderNumber}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                {/* Cash on Delivery Information */}
                {!isSatchelShipment &&
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                {t("COD (Cash On Delivery)")}
                            </Typography>
                            <Typography variant="body1">
                                {tempOrder?.amountPayable && tempOrder?.amountPayable > 0
                                    ? `${t("Yes")} - ${tempOrder.amountPayable} ${t("SAR")}`
                                    : t("No")
                                }
                            </Typography>
                        </Box>
                    </Grid>
                }

                {/* Warehouse Information */}
                <Grid item xs={12} md={6}>
                    <Box>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {t("Warehouse")}
                        </Typography>
                        <Typography variant="body1">
                            {isSatchelShipment ? order?.user?.name || '-' : order?.warehouse?.name || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Boxes Information */}
                {!isSatchelShipment &&
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
                                {t("Boxes Details")}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box>
                                <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                    {t("Boxes Count")}
                                </Typography>
                                <Typography variant="body1">
                                    {tempOrder?.packagesCount || tempOrder?.boxes?.length || "-"}
                                </Typography>
                            </Box>
                        </Grid>
                    </>
                }
                <Grid item xs={12} md={4}>
                    <Box>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {t("Total Weight")}
                        </Typography>
                        <Typography variant="body1">
                            {tempOrder?.weight ? `${tempOrder.weight} ${t("kg")}` : "-"}
                        </Typography>
                    </Box>
                </Grid>

                {order?.otoIntegration?.calculatedWeight && (
                    <Grid item xs={12} md={4}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                {t("Chargeable Weight")}
                            </Typography>
                            <Typography variant="body1">
                                {`${order.otoIntegration.calculatedWeight} ${t("kg")}`}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                {/* Box Details */}
                {tempOrder?.boxes?.length > 0 && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                {t("Box Dimensions")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {tempOrder.boxes.map((box, index) => (
                                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={2}>
                                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                {t("Box")} #{index + 1}
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {t("Box")} {index + 1}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={2.5}>
                                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                {t("Length")}
                                            </Typography>
                                            <Typography variant="body1">
                                                {box.dimensions?.length ? `${box.dimensions.length} cm` : "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={2.5}>
                                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                {t("Width")}
                                            </Typography>
                                            <Typography variant="body1">
                                                {box.dimensions?.width ? `${box.dimensions.width} cm` : "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={2.5}>
                                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                {t("Height")}
                                            </Typography>
                                            <Typography variant="body1">
                                                {box.dimensions?.height ? `${box.dimensions.height} cm` : "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={2.5}>
                                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                {t("Weight")}
                                            </Typography>
                                            <Typography variant="body1">
                                                {box.weight ? `${box.weight} ${t("kg")}` : "-"}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))}
                        </Grid>
                    </>
                )}

                {/* Order Items Information */}
                {tempOrder?.orderItems?.length > 0 && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                {t("Order Items")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {tempOrder.orderItems.map((item, index) => (
                                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                {t("Product name")}
                                            </Typography>
                                            <Typography variant="body1">
                                                {item.item || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                {t("SKU")}
                                            </Typography>
                                            <Typography variant="body1">
                                                {item.sku || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                {t("Count")}
                                            </Typography>
                                            <Typography variant="body1">
                                                {item.amount || "-"}
                                            </Typography>
                                        </Grid>
                                        {
                                            !isSatchelShipment &&
                                            <Grid item xs={12} md={3}>
                                                <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                    {t("Price")}
                                                </Typography>
                                                <Typography variant="body1">
                                                    {item.price ? `${item.price} ${t("SAR")}` : "-"}
                                                </Typography>
                                            </Grid>
                                        }
                                    </Grid>
                                </Box>
                            ))}
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    )
}